import { useEffect, useState } from 'react'
import { Modal } from './modal.jsx'
import type { JSX } from 'react'
import type { TenantKind } from '@qim/shared'
import { hideAccount, hideInstance, restoreAccount, restoreInstance } from '../api.js'
import { fetchAccountHealth, fetchPreflight } from '../api.js'
import { formatInstant } from '../format.js'
import { isHideable } from '@qim/shared'
import { pathFor } from '../routing.js'
import { AccountLink, mayOpenAccount } from './account-link.jsx'
import { QrveyBadge } from './account-settings.jsx'
import { HiddenNote, HideButton } from './hidden.jsx'
import { RegisterAccount } from './register-account.jsx'
import { useAccountRunFacts } from './account-run-facts.js'
import { RunProgress } from './run-progress.jsx'
import { StartRun } from './start-run.jsx'
import { DeploymentStatus } from './status.jsx'
import { OutsideHint } from './outside.jsx'
import { AwaitingHint } from './approvals.jsx'
import { ProtectionLock } from './lock.jsx'
import { AccountCost } from './account-cost.jsx'
import { SearchBackendLabel } from './search-backend.jsx'
import type { AccountHealth, AccountSummary, DeploymentSummary, RunSummary } from '../api.js'

/**
 * One customer: the accounts we may deploy into, and what is deployed.
 *
 * This screen is also where IM-28 finally appears. The health endpoint has
 * existed since then with nowhere to show its answer, which is why that story
 * sat in review — an API whose result nobody can see has not done the thing the
 * story asked for.
 */

type HealthState = AccountHealth | { status: 'checking' } | { status: 'unknown' }

/**
 * Whether an account needs setting up, in the words the button uses (IM-181).
 *
 * Only an answer that says "not ready" earns **Set up**. Checking and "could
 * not check" read as the neutral **Onboarding**: a failed check is usually
 * ours, and telling someone to set up an account that is working is the same
 * mistake as calling it broken.
 */
const needsSetUp = (health: HealthState): boolean =>
  health.status === 'not-onboarded' || health.status === 'needs-attention'

/**
 * Health is fetched per account, after the list renders — and drawn as two
 * cells: the answer, and what to do about it.
 *
 * Each check is two cross-account AWS calls, so blocking the whole screen on
 * them would make a customer with six accounts feel broken. The list is the
 * useful part and arrives first; health fills in.
 *
 * Two cells from one component, rather than a second reader, because both
 * cells show the same answer and it must stay one request per account: the
 * action column (IM-181) is worded by readiness, and fetching again for a
 * button would double the cross-account calls this screen makes.
 */
const HealthCells = ({
  baseUrl,
  tenantId,
  account,
  role,
  onOpen,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly account: AccountSummary
  readonly role: string
  readonly onOpen: (path: string) => void
}): JSX.Element => {
  const { accountKey, label } = account
  const [health, setHealth] = useState<HealthState>({ status: 'checking' })

  useEffect(() => {
    let live = true
    void fetchAccountHealth(baseUrl, tenantId, accountKey)
      .then((answer) => {
        if (live) setHealth(answer.health)
      })
      .catch(() => {
        // A failed check is not a failed account: the network, a cold Lambda, a
        // throttle. Saying "unknown" beats reporting a healthy account as broken.
        if (live) setHealth({ status: 'unknown' })
      })
    return () => {
      live = false
    }
  }, [baseUrl, tenantId, accountKey])

  /*
   * The named next step, as the way to the screen that carries it out. The
   * onboarding screen is where the template it asks for is handed over, so the
   * sentence and the place to act on it are one click apart, not two screens.
   */
  const nextStep = (step: string): JSX.Element => (
    <p className="health-next">
      <AccountLink tenantId={tenantId} accountKey={accountKey} role={role} onOpen={onOpen}>
        {step}
        {mayOpenAccount(role) ? <span aria-hidden="true"> →</span> : null}
      </AccountLink>
    </p>
  )

  const answer = ((): JSX.Element => {
    switch (health.status) {
      case 'checking':
        return <span role="status">Checking…</span>
      case 'ready':
        return (
          <span className="health health-ready">Ready — bootstrap v{health.bootstrapVersion}</span>
        )
      case 'not-onboarded':
        return (
          <>
            <span className="health health-not-onboarded">Not onboarded</span>
            {nextStep(health.nextStep)}
          </>
        )
      case 'needs-attention':
        return (
          <>
            <span className="health health-needs-attention">Needs attention — {health.detail}</span>
            {nextStep(health.nextStep)}
          </>
        )
      default:
        // Plain text and no link: this is our failure to look, not a step.
        return <span className="health health-unknown">Could not check just now</span>
    }
  })()

  const setUp = needsSetUp(health)
  return (
    <>
      <td>{answer}</td>
      <td>
        {/*
          The explicit way in (IM-181). The account's name was the only link to
          this screen, and nobody guessed it. Worded by state, not only
          coloured by it: Set up when the check says the account is not ready,
          Onboarding otherwise — including while checking and when the check
          failed, since guessing "Set up" there sends someone to fix an account
          that may be fine.
        */}
        {mayOpenAccount(role) ? (
          <AccountLink
            tenantId={tenantId}
            accountKey={accountKey}
            role={role}
            onOpen={onOpen}
            className={`button-link ${setUp ? 'primary' : 'ghost'}`}
            aria-label={`${setUp ? 'Set up' : 'Onboarding for'} ${label}`}
          >
            {setUp ? 'Set up' : 'Onboarding'}
          </AccountLink>
        ) : null}
      </td>
    </>
  )
}

/**
 * The run panel, and the account's regions fetched when it is opened.
 *
 * Two things are going on here, and both are the same lesson from IM-83: **a
 * closed `<details>` hides its children, and React still mounts them.** So the
 * child is rendered only once the panel has been opened, rather than relying on
 * the element to withhold it.
 *
 * That matters more than it did for a log, because this lookup is not free. The
 * regions come from the onboarding assessment, which reads into the customer's
 * account — and this screen has one of these per account. Mounting them all on
 * page load would make opening a customer with six accounts a fan-out of
 * cross-account calls for panels nobody opened, which is the shape of mistake
 * the health check is deliberately capped at two calls to avoid.
 *
 * A failed lookup is left undefined rather than surfaced as an error: the form
 * falls back to typing a region, and the panel someone opened to start a run
 * should not lead with a complaint about a list.
 */
const RunDisclosure = ({
  baseUrl,
  tenantId,
  account,
  role,
  environment,
  deployments,
  requesterIsStaff,
  onStarted,
  onOpen,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly account: AccountSummary
  readonly role: string
  readonly environment: string
  readonly deployments: readonly DeploymentSummary[]
  readonly requesterIsStaff: boolean
  readonly onStarted: (run: RunSummary) => void
  /** For the preflight's way to the onboarding screen (IM-181). */
  readonly onOpen: (path: string) => void
}): JSX.Element => {
  /*
   * Two flags, not one. `opened` is "this account has been asked about itself"
   * and never goes back — the facts are worth keeping once fetched, so closing
   * and reopening the dialog costs no second cross-account call. `showing` is
   * whether the dialog is on screen.
   */
  const [opened, setOpened] = useState(false)
  const [showing, setShowing] = useState(false)
  const { regions, registryFromAccount, installerImages, installerChoices } = useAccountRunFacts(
    baseUrl,
    tenantId,
    account.accountKey,
    opened,
  )

  return (
    /*
     * Behind a button and in a modal (IM-146), still deliberate: this is the
     * only control in the console that reaches a customer's infrastructure, and
     * it should take a click to open rather than sitting beside a status table.
     * The account is asked about itself when it opens, not on render — one
     * panel per account would otherwise fan out a cross-account call per row.
     */
    <>
      {/*
        Ghost, not primary. There is one of these per account, and three solid
        accent buttons in a row is the panel shouting at somebody who came to
        read a table — the same restraint that kept this behind a disclosure in
        the first place.
      */}
      <button
        type="button"
        className="ghost"
        onClick={() => {
          setOpened(true)
          setShowing(true)
        }}
      >
        Run&hellip;
      </button>
      {showing && opened ? (
        <Modal title={`Run something in ${account.label}`} onCancel={() => setShowing(false)} wide>
          <StartRun
            baseUrl={baseUrl}
            tenantId={tenantId}
            accountKey={account.accountKey}
            role={role}
            environment={environment}
            {...(regions === undefined ? {} : { regions })}
            {...(registryFromAccount === undefined ? {} : { registryFromAccount })}
            {...(installerImages === undefined ? {} : { installerImages })}
            {...(installerChoices === undefined ? {} : { installerChoices })}
            deployments={deployments}
            requesterIsStaff={requesterIsStaff}
            onOpen={onOpen}
            // Only once the dialog is open, like the regions above (IM-36).
            preflight={(command, variables, region) =>
              fetchPreflight(baseUrl, tenantId, account.accountKey, command, variables, region)
            }
            onStarted={(run) => {
              // Closed on start, so the progress panel behind it is what the
              // caller is left looking at rather than the form they just filled.
              setShowing(false)
              onStarted(run)
            }}
          />
        </Modal>
      ) : null}
    </>
  )
}

export const Customer = ({
  baseUrl,
  tenantId,
  kind,
  role,
  environment,
  accounts,
  deployments,
  onOpen,
  onChanged,
  viewer,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  /** Which console this is, for the run form's defaults (IM-175). */
  readonly environment: string
  /**
   * The customer's kind, from the tenant list already loaded for ⌘K. Absent
   * while that list is still arriving, which renders no badge rather than a
   * wrong one.
   */
  readonly kind?: TenantKind | undefined
  /** Decides which commands are offered. The API checks them regardless. */
  readonly role: string
  readonly accounts: readonly AccountSummary[]
  readonly deployments: readonly DeploymentSummary[]
  readonly onOpen: (path: string) => void
  /** Reloads accounts and deployments, so a new registration appears. */
  readonly onChanged: () => void
  /**
   * Who is looking, for the approval queue (IM-102): staff cannot decide for a
   * customer, and nobody approves their own request. Absent is staff with no
   * address, which offers no Approve that could be self-approval.
   */
  readonly viewer?: { readonly email: string; readonly isStaff: boolean }
}): JSX.Element => {
  const [started, setStarted] = useState<RunSummary | undefined>(undefined)

  /**
   * A run that has started is a deployment the console now knows more about.
   *
   * Re-reading here is what lets the *next* run remember this one's answers
   * (IM-117). The run record is written before dispatch and carries the
   * variables it was asked for, and a deployment is derived from its runs — so
   * the moment a plan starts, the customer's deployment list can describe an
   * instance that did not exist a second ago. Without this the list is whatever
   * was fetched when the screen loaded, so a plan against a new id leaves the
   * form nothing to match and the apply that follows starts from blank.
   *
   * It re-reads rather than splicing the deployment in locally, for the reason
   * `onChanged` already exists: what the caller may see is the API's decision.
   *
   * The screen stays mounted through it — `loadedFor` only blanks on a
   * navigation — so `started` below keeps polling and the form keeps its values.
   */
  const onRunStarted = (run: RunSummary): void => {
    setStarted(run)
    onChanged()
  }

  return (
    <>
      <section className="panel">
        <h2>Cloud accounts</h2>
        {accounts.length === 0 ? (
          <p role="status">No accounts are registered for this customer yet.</p>
        ) : (
          // Wide content scrolls in its own box so the page body never scrolls
          // sideways — the health column carries a sentence.
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Account</th>
                  <th scope="col">Cloud</th>
                  <th scope="col">Access</th>
                  <th scope="col">
                    <span className="visually-hidden">Onboarding</span>
                  </th>
                  <th scope="col">
                    <span className="visually-hidden">Hide</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.accountKey}>
                    <td>
                      {/* Through to the onboarding screen, which is where the
                      reason an account is not ready actually lives. Kept beside
                      the explicit action (IM-181): the name is what people
                      reach for once they know it is a link. */}
                      <AccountLink
                        tenantId={tenantId}
                        accountKey={account.accountKey}
                        label={account.label}
                        role={role}
                        onOpen={onOpen}
                      />{' '}
                      {/* Qrvey's own, said beside every account the engineer
                        might pick from — the customer's kind since IM-149, so
                        all of a Qrvey customer's accounts carry it. */}
                      <QrveyBadge kind={kind} />
                      {account.settings?.newInstancesBlocked?.value === true ? (
                        <p className="muted">Takes no new instances</p>
                      ) : null}
                      <p className="muted">{account.accountKey}</p>
                      {account.deletedAt === undefined ? null : (
                        <HiddenNote at={account.deletedAt} by={account.deletedBy} />
                      )}
                    </td>
                    <td>{account.cloud}</td>
                    <HealthCells
                      baseUrl={baseUrl}
                      tenantId={tenantId}
                      account={account}
                      role={role}
                      onOpen={onOpen}
                    />
                    <td>
                      {/*
                        Both of this row's actions in the row (IM-146). The run
                        trigger used to sit in a stack of disclosures under the
                        table, one per account, so the control and the account it
                        acts on were separated by every other account.
                      */}
                      <div className="row-actions">
                        <RunDisclosure
                          baseUrl={baseUrl}
                          tenantId={tenantId}
                          account={account}
                          role={role}
                          environment={environment}
                          deployments={deployments}
                          requesterIsStaff={viewer?.isStaff ?? true}
                          onStarted={onRunStarted}
                          onOpen={onOpen}
                        />
                        <HideButton
                          role={role}
                          hidden={account.deletedAt !== undefined}
                          what="account"
                          name={account.label}
                          confirm
                          onHide={() => hideAccount(baseUrl, tenantId, account.accountKey)}
                          onRestore={() => restoreAccount(baseUrl, tenantId, account.accountKey)}
                          onChanged={onChanged}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {started === undefined ? null : (
          <RunProgress baseUrl={baseUrl} tenantId={tenantId} run={started} />
        )}

        <RegisterAccount
          baseUrl={baseUrl}
          tenantId={tenantId}
          role={role}
          onRegistered={onChanged}
          onOpen={onOpen}
        />
      </section>

      {/* Approvals and the customer's own people are tabs of their own since
        the secondary navigation — so neither is read until it is opened. */}

      <section className="panel">
        <h2>Deployments</h2>
        {deployments.length === 0 ? (
          <p role="status">
            Nothing has been deployed for this customer yet. A deployment appears here once a run
            names one.
          </p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Deployment</th>
                  <th scope="col">Region</th>
                  <th scope="col">Status</th>
                  <th scope="col">Version</th>
                  <th scope="col">Search</th>
                  <th scope="col">Last touched</th>
                  <th scope="col">
                    <span className="visually-hidden">Hide</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {deployments.map((deployment) => {
                  const href = pathFor({
                    screen: 'deployment',
                    tenantId,
                    deploymentId: deployment.deploymentId,
                  })
                  return (
                    <tr key={deployment.deploymentId}>
                      <td>
                        <a
                          href={href}
                          onClick={(event) => {
                            if (
                              event.metaKey ||
                              event.ctrlKey ||
                              event.shiftKey ||
                              event.button !== 0
                            ) {
                              return
                            }
                            event.preventDefault()
                            onOpen(href)
                          }}
                        >
                          {deployment.deploymentId}
                        </a>
                        <ProtectionLock mark={deployment.protected} kind={kind} />
                      </td>
                      {/* A dash is "recorded before regions were" (IM-92), not
                      "no region" — every deployment is in one. Shown here and
                      not only on the detail screen because one customer's
                      instances can be in several, and which is which is the
                      first thing a support engineer needs. */}
                      <td>{deployment.region ?? '—'}</td>
                      <td>
                        <DeploymentStatus
                          status={deployment.status}
                          notFound={deployment.scanSaysGone !== undefined}
                        />{' '}
                        {/* Why a destroyed instance never ran a destroy here, or
                          that the scan cannot find a live one (IM-156). */}
                        <OutsideHint deployment={deployment} />
                        <AwaitingHint deployment={deployment} />
                      </td>
                      {/* Absent means nothing has been applied successfully, which
                      is not the same as an unknown version — see the projection. */}
                      <td>{deployment.chartVersion ?? '—'}</td>
                      {/* Read like the version, from the newest succeeded
                        apply, and marked when it is only a request (IM-182). */}
                      <td>
                        <SearchBackendLabel search={deployment.search} />
                      </td>
                      <td>
                        {deployment.lastRun === undefined ? (
                          /* Adopted and never run here (IM-140). Saying when it
                             was brought in beats an empty cell, which reads as a
                             rendering fault, and beats a dash, which reads as
                             "nothing is known". */
                          <>
                            <span className="muted">No run here yet</span>
                            {deployment.adopted === undefined ? null : (
                              <p className="muted">
                                brought in by {deployment.adopted.by} on{' '}
                                <time dateTime={deployment.adopted.at}>
                                  {formatInstant(deployment.adopted.at)}
                                </time>
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            <time dateTime={deployment.lastRun.requestedAt}>
                              {formatInstant(deployment.lastRun.requestedAt)}
                            </time>
                            <p className="muted">
                              {deployment.lastRun.command} by {deployment.lastRun.requestedBy}
                            </p>
                          </>
                        )}
                      </td>
                      <td>
                        {/*
                          Offered only for an instance that can actually be
                          hidden — `destroyed` or `never-applied`. The rule is
                          the API's and it refuses the rest with a 409, but a
                          button that always fails is a worse way to say no than
                          not offering one, which is the same reasoning that
                          hides commands a caller's role cannot use.

                          And no confirmation, unlike the customer and the
                          account: IM-79's point is that gating the trivially
                          reversible act is how a dialog stops being read.
                        */}
                        {!isHideable(deployment.status) &&
                        deployment.hidden === undefined ? null : (
                          <HideButton
                            role={role}
                            hidden={deployment.hidden !== undefined}
                            what="instance"
                            name={deployment.deploymentId}
                            confirm={false}
                            onHide={() =>
                              hideInstance(
                                baseUrl,
                                tenantId,
                                deployment.accountKey,
                                deployment.deploymentId,
                              )
                            }
                            onRestore={() =>
                              restoreInstance(
                                baseUrl,
                                tenantId,
                                deployment.accountKey,
                                deployment.deploymentId,
                              )
                            }
                            onChanged={onChanged}
                          />
                        )}
                        {deployment.hidden === undefined ? null : (
                          <HiddenNote
                            at={deployment.hidden.at}
                            by={deployment.hidden.by}
                            status={deployment.hidden.status}
                          />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}

/**
 * A customer's Cost tab: each of their accounts' month so far (IM-130, IM-131).
 *
 * One `AccountCost` per account, each behind its own button, because every read
 * here bills **the customer's** account about a cent — the reason IM-130 made
 * cost something asked for, which a tab does not change. Opening the tab reads
 * nothing; the account list it lists was already read to render the screen.
 */
export const CustomerCost = ({
  baseUrl,
  tenantId,
  role,
  accounts,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly role: string
  readonly accounts: readonly AccountSummary[]
}): JSX.Element => {
  // A hidden account is out of the cost question the way `/cost` leaves it out
  // of the estate's total — and a button that charges for it would be a trap.
  const shown = accounts.filter((account) => account.deletedAt === undefined)
  return shown.length === 0 ? (
    <section className="panel">
      <h2>Cost</h2>
      <p role="status">No accounts are registered for this customer yet.</p>
    </section>
  ) : (
    <>
      {shown.map((account) => (
        <AccountCost
          key={account.accountKey}
          baseUrl={baseUrl}
          tenantId={tenantId}
          accountKey={account.accountKey}
          role={role}
          title={`${account.label} · ${account.accountKey}`}
        />
      ))}
    </>
  )
}
